import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../ui';

/**
 * Markdown renderer for AI chat answers, shared by the /agente page and the
 * ⌘K drawer (both go through <Thread>). Uses `react-markdown` + `remark-gfm`
 * (GFM = tables, strikethrough, task lists, autolinks), which render to React
 * elements — **no innerHTML, no eval**, so it's safe under the app CSP
 * (`script-src 'self'`). Raw HTML from the model is intentionally NOT enabled
 * (no `rehype-raw`), so model output can never inject markup.
 *
 * Everything is styled through the `components` map with the maroon design
 * system, sized for a chat bubble (compact, not full-article scale). Code
 * blocks and tables scroll horizontally inside their own container so a wide
 * payload never blows out the narrow drawer bubble; long unbroken strings wrap.
 */

/** Tailwind-styled element map — maroon design system, chat-bubble scale. */
const components: Components = {
  // Headings — serif/bold, scaled down for a bubble, tight top margin.
  h1: ({ children }) => (
    <h1 className="mb-1 mt-3 font-serif text-[15px] font-bold leading-snug text-ink">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-1 mt-3 text-sm font-bold leading-snug text-ink">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-0.5 mt-2 text-[13px] font-semibold leading-snug text-ink">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="mb-0.5 mt-2 text-[13px] font-semibold leading-snug text-ink-soft">{children}</h4>
  ),
  h5: ({ children }) => (
    <h5 className="mb-0.5 mt-2 text-xs font-semibold uppercase tracking-wide text-ink-soft">
      {children}
    </h5>
  ),
  h6: ({ children }) => (
    <h6 className="mb-0.5 mt-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">
      {children}
    </h6>
  ),

  // Paragraph — normal leading, the existing bubble spacing.
  p: ({ children }) => <p className="my-2 leading-relaxed">{children}</p>,

  // Inline emphasis.
  strong: ({ children }) => <strong className="font-semibold text-ink">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  del: ({ children }) => <del className="text-ink-soft line-through">{children}</del>,

  // Lists — disc/decimal, indented, nesting works via the recursive ul/ol.
  ul: ({ children }) => <ul className="my-2 list-disc space-y-1 pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="my-2 list-decimal space-y-1 pl-5">{children}</ol>,
  li: ({ children, className }) => (
    <li className={cn('leading-relaxed', className?.includes('task-list-item') && 'list-none')}>
      {children}
    </li>
  ),
  // GFM task-list checkbox — read-only, on-brand accent.
  input: ({ type, checked }) =>
    type === 'checkbox' ? (
      <input
        type="checkbox"
        checked={!!checked}
        readOnly
        disabled
        className="mr-1.5 translate-y-px accent-primary"
      />
    ) : null,

  // Links — maroon, always open in a new tab, safe rel; long URLs wrap.
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="break-words text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary"
    >
      {children}
    </a>
  ),

  // Blockquote — left maroon rule, soft italic ink.
  blockquote: ({ children }) => (
    <blockquote className="my-2 border-l-2 border-primary/30 pl-3 italic text-ink-soft">
      {children}
    </blockquote>
  ),

  // Code — inline chip vs. fenced block. A code node is a block when it carries a
  // language class (```lang) OR spans multiple lines; otherwise it's inline `code`.
  pre: ({ children }) => (
    <pre className="my-2 overflow-x-auto whitespace-pre rounded-lg border border-line bg-canvas p-3 font-mono text-xs leading-relaxed text-ink">
      {children}
    </pre>
  ),
  code: ({ className, children }) => {
    const text = String(children ?? '');
    const isBlock = /language-/.test(className ?? '') || text.includes('\n');
    if (isBlock) {
      // Inside the styled <pre> above — just the raw content, no inline chrome.
      return <code className="font-mono">{text.replace(/\n$/, '')}</code>;
    }
    return (
      <code className="whitespace-normal break-words rounded border border-line bg-canvas px-1 py-0.5 font-mono text-[0.85em] text-ink">
        {children}
      </code>
    );
  },

  // Tables — scroll inside a bounded wrapper so wide tables never overflow the
  // bubble; a min-width keeps columns readable while the wrapper scrolls.
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto">
      <table className="w-full min-w-[28rem] border-collapse text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-canvas">{children}</thead>,
  th: ({ children }) => (
    <th className="border border-line px-2.5 py-1.5 text-left align-top font-semibold text-ink">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-line px-2.5 py-1.5 align-top text-ink-soft">{children}</td>
  ),

  // Horizontal rule.
  hr: () => <hr className="my-3 border-t border-line" />,

  // Images — the CSP blocks external hosts, so never emit a broken <img>. Show a
  // safe inline placeholder using the alt text instead.
  img: ({ alt }) => (
    <span className="rounded bg-canvas px-1.5 py-0.5 font-mono text-[0.85em] text-ink-faint">
      {alt ? `[imagen: ${alt}]` : '[imagen]'}
    </span>
  ),
};

/** Rich markdown for chat answers — headings, emphasis, lists, code, tables, GFM. */
export function ChatMarkdown({ text }: { text: string }) {
  return (
    <div className="break-words text-sm leading-relaxed text-ink [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  );
}
